"use client";

import React, { Suspense, useState, useCallback, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { TRAINING_ZONE_PAGES } from "@/lib/docs-config";
import { cn } from "@/lib/utils";
import { X, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const docComponents: Record<string, React.ComponentType> = {
  "/docs/getting-started": dynamic(
    () => import("@/components/docs/getting-started-content").then((m) => ({ default: m.GettingStartedContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/adding-games": dynamic(
    () => import("@/components/docs/adding-games-content").then((m) => ({ default: m.AddingGamesContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/schedule-import": dynamic(
    () => import("@/components/docs/schedule-import-content").then((m) => ({ default: m.ScheduleImportContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/payments": dynamic(
    () => import("@/components/docs/payments-content").then((m) => ({ default: m.PaymentsContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/calendar": dynamic(
    () => import("@/components/docs/calendar-content").then((m) => ({ default: m.CalendarContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/locations": dynamic(
    () => import("@/components/docs/locations-content").then((m) => ({ default: m.LocationsContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/partners": dynamic(
    () => import("@/components/docs/partners-content").then((m) => ({ default: m.PartnersContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/shortcuts": dynamic(
    () => import("@/components/docs/shortcuts-content").then((m) => ({ default: m.ShortcutsContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/games-locations": dynamic(
    () => import("@/components/docs/games-locations-content").then((m) => ({ default: m.GamesLocationsContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/partners-positions": dynamic(
    () => import("@/components/docs/partners-positions-content").then((m) => ({ default: m.PartnersPositionsContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/orgs-teams": dynamic(
    () => import("@/components/docs/orgs-teams-content").then((m) => ({ default: m.OrgsTeamsContent })),
    { loading: () => <DocLoading /> },
  ),
  "/docs/manage-finances": dynamic(
    () => import("@/components/docs/manage-finances-content").then((m) => ({ default: m.ManageFinancesContent })),
    { loading: () => <DocLoading /> },
  ),
};

function DocLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

const FONT_SIZES = [0.55, 0.6, 0.65, 0.725, 0.8, 0.875, 0.95] as const;
const DEFAULT_FONT_INDEX = 1; // 0.6rem — compact by default, user can scale up
const MIN_WIDTH = 280;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 384; // 24rem

interface TrainingZonePanelProps {
  open: boolean;
  onClose: () => void;
  selectedDoc: string;
  onSelectDoc: (href: string) => void;
}

const SIDEBAR_HREFS = new Set([
  "/dashboard", "/", "/games", "/locations", "/partners",
  "/organizations", "/teams", "/finances", "/deals",
  "/account", "/settings", "/support", "/admin",
]);

export function TrainingZonePanel({
  open,
  onClose,
  selectedDoc,
  onSelectDoc,
}: TrainingZonePanelProps) {
  const DocComponent = docComponents[selectedDoc];
  const [fontIdx, setFontIdx] = useState(DEFAULT_FONT_INDEX);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(DEFAULT_WIDTH);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [panelWidth]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!isDragging.current) return;
      const delta = e.clientX - dragStartX.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      setPanelWidth(newWidth);
    }
    function onMouseUp() {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    let highlighted: HTMLElement | null = null;

    function clearHighlight() {
      if (highlighted) {
        highlighted.removeAttribute("data-tz-highlight");
        highlighted = null;
      }
    }

    function onEnter(e: Event) {
      const target = (e.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;
      if (!target) return;
      const href = target.getAttribute("href");
      if (!href || !SIDEBAR_HREFS.has(href)) return;
      clearHighlight();
      const menuBtn = document.querySelector(`[data-menu-href="${href}"]`) as HTMLElement | null;
      if (menuBtn) {
        menuBtn.setAttribute("data-tz-highlight", "true");
        highlighted = menuBtn;
      }
    }

    function onLeave(e: Event) {
      const target = (e.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;
      if (!target) return;
      clearHighlight();
    }

    el.addEventListener("mouseover", onEnter);
    el.addEventListener("mouseout", onLeave);
    return () => {
      clearHighlight();
      el.removeEventListener("mouseover", onEnter);
      el.removeEventListener("mouseout", onLeave);
    };
  }, [open, selectedDoc]);

  if (!open) return null;

  const fontSize = FONT_SIZES[fontIdx];
  const canDecrease = fontIdx > 0;
  const canIncrease = fontIdx < FONT_SIZES.length - 1;

  return (
    <div className="relative flex h-svh flex-col border-r bg-background" style={{ width: panelWidth }}>
      {/* Header: text size controls + close button (replaces redundant "Training Zone" title) */}
      <div className="flex items-center justify-between border-b px-3 py-2 bg-yellow-400/10 dark:bg-yellow-500/10">
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => canDecrease && setFontIdx((i) => i - 1)}
            disabled={!canDecrease}
            className={cn(
              "flex items-center justify-center rounded px-1.5 py-1 transition-colors",
              canDecrease
                ? "hover:bg-yellow-400/20 text-yellow-700 dark:text-yellow-300"
                : "text-muted-foreground/40 cursor-not-allowed",
            )}
            aria-label="Decrease font size"
          >
            <span className="text-xs font-serif font-bold leading-none">A</span>
            <Minus className="h-2.5 w-2.5 ml-0.5" />
          </button>
          <span className="text-[10px] tabular-nums text-yellow-700/70 dark:text-yellow-300/70 w-8 text-center">
            {Math.round(fontSize * 100)}%
          </span>
          <button
            onClick={() => canIncrease && setFontIdx((i) => i + 1)}
            disabled={!canIncrease}
            className={cn(
              "flex items-center justify-center rounded px-1.5 py-1 transition-colors",
              canIncrease
                ? "hover:bg-yellow-400/20 text-yellow-700 dark:text-yellow-300"
                : "text-muted-foreground/40 cursor-not-allowed",
            )}
            aria-label="Increase font size"
          >
            <span className="text-base font-serif font-bold leading-none">A</span>
            <Plus className="h-2.5 w-2.5 ml-0.5" />
          </button>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-400/20" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Doc navigation — 2-column, 4-row grid */}
      <nav className="border-b px-2 py-2">
        <div className="grid grid-cols-2 gap-0.5">
          {TRAINING_ZONE_PAGES.map((page) => (
            <button
              key={page.href}
              onClick={() => onSelectDoc(page.href)}
              className={cn(
                "rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors text-left truncate",
                selectedDoc === page.href
                  ? "bg-yellow-400/20 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300"
                  : "text-muted-foreground hover:bg-yellow-400/10 hover:text-yellow-700 dark:hover:bg-yellow-500/10 dark:hover:text-yellow-300",
              )}
            >
              {page.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Doc content */}
      <ScrollArea className="flex-1 min-h-0">
        <div ref={contentRef} className="px-3 py-4 training-zone-content" style={{ fontSize: `${fontSize}rem` }}>
          <Suspense fallback={<DocLoading />}>
            {DocComponent ? <DocComponent /> : <DocLoading />}
          </Suspense>
        </div>
        <style jsx>{`
          .training-zone-content :global(h1) { font-size: 1.4em; margin-bottom: 0.3em; }
          .training-zone-content :global(h2) { font-size: 1.15em; margin-bottom: 0.25em; }
          .training-zone-content :global(h3) { font-size: 1.05em; margin-bottom: 0.2em; }
          .training-zone-content :global(p) { margin-bottom: 0.4em; }
          .training-zone-content :global(ul), .training-zone-content :global(ol) { margin-bottom: 0.4em; }
          .training-zone-content :global(a[href^="/"]) {
            border-bottom: 1px dashed currentColor;
            transition: border-color 150ms, color 150ms;
          }
          .training-zone-content :global(a[href^="/"]:hover) {
            border-bottom-style: solid;
          }
        `}</style>
      </ScrollArea>

      {/* Resize handle on right edge */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize group/resize z-20 hover:w-2 transition-[width]"
      >
        {/* Visible track */}
        <div className="absolute inset-y-0 right-0 w-1 bg-border group-hover/resize:bg-primary/40" />
        {/* Grab indicator in the middle */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 flex flex-col gap-[3px] items-center w-1.5 py-2 opacity-0 group-hover/resize:opacity-100">
          <div className="w-1 h-1 rounded-full bg-primary/50" />
          <div className="w-1 h-1 rounded-full bg-primary/50" />
          <div className="w-1 h-1 rounded-full bg-primary/50" />
          <div className="w-1 h-1 rounded-full bg-primary/50" />
          <div className="w-1 h-1 rounded-full bg-primary/50" />
        </div>
      </div>
    </div>
  );
}
